/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent, ColumnDefinition } from 'tabulator-tables';

/**
 * internal dependencies
 */
import { ITerminal } from '../../../models/terminal.interface';
import useAuth from '../../../hooks/auth-hooks';

/**
 * Displays the terminal list table on the terminals page
 * 
 * @param element table element
 * @param data terminal data
 * 
 * @returns the initialized Tabulator table
 */
export const displayTerminalList = ( element: HTMLElement, data: ITerminal[] ): TabulatorFull => {
  const { accessLevel, accessType } = useAuth()!;

  /**
   * Displays a button that links to the individual terminals page
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the link button
   */
  const link_button = (cell: CellComponent, formatterParams: any) => {
    const { terminalId, partnerId, terminalName } = cell.getData() as ITerminal;
    let userPrev = accessType === 'E' ? 'edit' : 'view';
    return `<a class='aafp_btn-table btn_theme-primary' 
            href='/${userPrev}-terminal/?terminalAction=${userPrev}&terminalID=${terminalId}&partnerID=${partnerId}' 
            data-toggle="tooltip" 
            data-placement="bottom" 
            title="View Terminal ${terminalName}"><i class="far fa-eye"></i></a>`;
  }

  /**
   * Formats the status to display a pill based upon the status
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted status
   */
  const statusFormat = (cell: CellComponent, formatterParams: any) => {
    const { status } = cell.getData() as ITerminal;
    const terminalStatus = status === 'Active' ? 'Active' : 'Inactive';
    return `<span class="terminal-${terminalStatus.toLowerCase()}">${terminalStatus}</span>`;   
  }

  /**
   * Formats the online status to display a pill based upon the status
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted online status
   */
  const onlineStatusFormat = (cell: CellComponent, formatterParams: any) => {
    const { OnlineStatus } = cell.getData() as ITerminal;
    const terminalStates: any = {
      'Online': { text: 'Online', class: 'active' },
      'Offline': { text: 'Offline', class: 'inactive' },
      'Not Responding': { text: 'Not Responding', class: 'not-responding' }
    };

    const terminalStatus = terminalStates[OnlineStatus] || terminalStates['Not Responding'];

    return `<span class="terminal-${terminalStatus.class}">${terminalStatus.text}</span>`;   
  }

  let displayField = true;
  if ( accessLevel === 'P') {
    displayField = false;
  }

  const baseTableColumns: ColumnDefinition[] = [
    { title: "terminalId", field: "terminalId", visible: false, download: false },
    { title: 'Site Code', field: 'terminalId', cssClass: 'aafp_table-font-size aafp_column-button-wide', width: 30, resizable: false, vertAlign: 'middle' },
    { title: 'Name', field: 'terminalName', width: 170, resizable: false, vertAlign: 'middle' },
    { title: 'Description', field: 'terminalDescription', visible: false, download: true },
    { title: 'Partner', field: 'partnerName', cssClass: "aafp_column-button-ex-wide", resizable: false, vertAlign: 'middle' },
    { title: 'Billing Type', field: 'billingType', cssClass: "aafp_column-button-ex-wide", resizable: false, visible: displayField, download: displayField, vertAlign: 'middle' },
    { title: 'Cost Cents', field: 'costCents', visible: false, download: true },
    { title: 'Default Margin Cents', field: 'defaultMarginCents', visible: false, download: true},
    { title: 'Default Price', field: 'defaultPrice', visible: false, download: true},
    { title: 'Status', field: 'status', formatter: statusFormat, width: 30, cssClass: "aafp_column-button-wide", resizable: false, vertAlign: 'middle' },
    { title: 'Online Status', field: 'OnlineStatus', formatter: onlineStatusFormat, cssClass: "aafp_column-button-ex-wide", resizable: false, vertAlign: 'middle' },
    { title: 'Last Heartbeat', field: 'LastHeartbeat', visible: false, download: true},
    { title: 'Last Heartbeat Result', field: 'LastHeartbeatResult', visible: false, download: true},
    { title: 'Last Transaction', field: 'lastTransaction', visible: false, download: true},
    { title: 'Street 1', field: 'addressStreet1', visible: false, download: true},
    { title: 'Street 2', field: 'addressStreet2', visible: false, download: true},
    { title: 'City', field: 'addressCity', visible: false, download: true},
    { title: 'State', field: 'addressState', visible: false, download: true},
    { title: 'Zip', field: 'addressZip', visible: false, download: true},
    { title: 'Full Address', field: 'siteAddress', visible: false, download: true},
    { title: 'Region', field: 'region', visible: false, download: true},
    { title: 'Servicing Branch', field: 'servicingBranch', visible: false, download: true},
    { title: 'Customer List', field: 'customerList', visible: false, download: true},
    { title: 'Notes', field: 'notes', visible: false, download: true},
    { title: 'Contact 1 Name', field: 'contact1Name', visible: false, download: true },
    { title: 'Contact 1 Phone', field: 'contact1Phone', visible: false, download: true },
    { title: 'Contact 1 Email', field: 'contact1Email', visible: false, download: true },
    { title: 'Contact 2 Name', field: 'contact2Name', visible: false, download: true },
    { title: 'Contact 2 Phone', field: 'contact2Phone', visible: false, download: true },
    { title: 'Contact 2 Email', field: 'contact2Email', visible: false, download: true },
    { title: 'Contact 3 Name', field: 'contact3Name', visible: false, download: true },
    { title: 'Contact 3 Phone', field: 'contact3Phone', visible: false, download: true },
    { title: 'Contact 3 Email', field: 'contact3Email', visible: false, download: true },
    { title: 'Access Description', field: 'accessDescription', visible: false, download: true },
    { title: '', field: "LinkButton", formatter: link_button, width: 20, cssClass: 'aafp_column-button', hozAlign: 'center', headerSort: false, resizable: false, download: false },
  ];

  const adminColumns: ColumnDefinition[] = [
    { title: 'Partner ID', field: 'partnerId', visible: false, download: true },
    { title: 'Transaction Fee', field: 'transactionFee', visible: false, download: true},
    { title: 'Terminal Fee', field: 'terminalFee', visible: false, download: true},
    { title: 'National', field: 'national', visible: false, download: true},
    { title: 'Internal Notes', field: 'internalNotes', visible: false, download: true},
    { title: 'IP Address', field: 'ipAddress', visible: false, download: true},
    { title: 'Serial Number', field: 'serialNumber', visible: false, download: true},
    { title: 'Order Date', field: 'orderDate', visible: false, download: true},
    { title: 'Monitor Serial Number', field: 'monitorSerialNumber', visible: false, download: true},
    { title: 'GPS Latitude', field: 'gpsLatitude', visible: false, download: true},
    { title: 'GPS Longitude', field: 'gpsLongitude', visible: false, download: true},
  ];

  const getTableColumns = (accessLevel: string): ColumnDefinition[] => {
    if (accessLevel === 'A') {
      return [...baseTableColumns, ...adminColumns];
    } else {
      return baseTableColumns;
    }
  };

  const tableColumns = getTableColumns(accessLevel);

  const options: Options = {
    index:"terminalId",
    data: data,
    placeholder: "No Data Available",
    placeholderHeaderFilter: "No Matching Data",
    pagination: true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 75],
    selectableRows: false,
    paginationCounter: "rows",
    downloadRowRange:"all",
    columns: tableColumns
  };
  
  const terminalTableGrid = new TabulatorFull(element, options);
  return terminalTableGrid;
}